/**
 * @module Actions
 * @description Action file containing action constants and generators. These actions are used in Redux architecture and are triggered by the view layer.
 */

/**
 * @typedef SignRedirectData
 * @type {object}
 * @property {string} redirect
 * @property {number} duration
 */

/**
 * @typedef SignRequestData
 * @type {object}
 * @property {string} id
 * @property {string} redirect
 * @property {number} duration
 */

/**
 * @typedef DimensionsData
 * @type {object}
 * @property {number} width
 * @property {number} height
 * @property {number} top
 * @property {number} right
 * @property {number} bottom
 * @property {number} left
 */

/**
 * @typedef ScrollData
 * @type {object}
 * @property {number} x
 * @property {number} y
 */

export const SIGN_RESET = 'SIGN_RESET';
export const SIGN_LOADER_SHOW = 'SIGN_LOADER_SHOW';
export const SIGN_LOADER_HIDE = 'SIGN_LOADER_HIDE';
export const SIGN_LOADER_MODE_UPDATE = 'SIGN_LOADER_MODE_UPDATE';
export const SIGN_KEYPAD_CLEAR = 'SIGN_KEYPAD_CLEAR';
export const SIGN_KEYPAD_KEY_ADD = 'SIGN_KEYPAD_KEY_ADD';
export const SIGN_KEYPAD_KEY_REMOVE = 'SIGN_KEYPAD_KEY_REMOVE';
export const SIGN_KEYPAD_MODE_UPDATE = 'SIGN_KEYPAD_MODE_UPDATE';
export const SIGN_KEYPAD_KEYS_SHOW = 'SIGN_KEYPAD_KEYS_SHOW';
export const SIGN_ANIMATIONS_START = 'SIGN_ANIMATIONS_START';
export const SIGN_ANIMATIONS_STOP = 'SIGN_ANIMATIONS_STOP';
export const SIGN_ANIMATIONS_CANCELED = 'SIGN_ANIMATIONS_CANCELED';
export const SIGN_REDIRECT_START = 'SIGN_REDIRECT_START';
export const SIGN_REDIRECT_STOP = 'SIGN_REDIRECT_STOP';
export const SIGN_FETCH_REQUESTED = 'SIGN_FETCH_REQUESTED';
export const SIGN_FETCH_SUCCEEDED = 'SIGN_FETCH_SUCCEEDED';
export const SIGN_FETCH_FAILED = 'SIGN_FETCH_FAILED';
export const VENDOR_FETCH_REQUESTED = 'VENDOR_FETCH_REQUESTED';
export const VENDOR_FETCH_SUCCEEDED = 'VENDOR_FETCH_SUCCEEDED';
export const VENDOR_FETCH_FAILED = 'VENDOR_FETCH_FAILED';
export const SEND_NUMBER_REQUESTED = 'SEND_NUMBER_REQUESTED';
export const SEND_NUMBER_SUCCEEDED = 'SEND_NUMBER_SUCCEEDED';
export const SEND_NUMBER_FAILED = 'SEND_NUMBER_FAILED';
export const DIMENSIONS_UPDATE = 'DIMENSIONS_UPDATE';
export const SCROLL_UPDATE = 'SCROLL_UPDATE';

/**
 * Action to start initial animations when the sign page loads.
 *
 * @param {SignRedirectData} data
 */
export function signAnimationsStart({ redirect, duration }) {
  return { type: SIGN_ANIMATIONS_START, data: { redirect, duration } };
}

/** Action to stop initial animations on the sign page. For instance, this can be triggered when navigating to another page to prevent events from continuing to fire in the background.  */
export function signAnimationsStop() {
  return { type: SIGN_ANIMATIONS_STOP };
}

/** Action used after initial animations have been canceled. */
export function signAnimationsCanceled() {
  return { type: SIGN_ANIMATIONS_CANCELED };
}

/**
 * Action to change the loader mode.
 *
 * @param {number} mode -
 * 0. Background visible and loader invisible by default.
 * 1. Loader is visible.
 * 2. Loader is invisible. Line one and sign are visible.
 * 3. Loader is invisible. Line one and sign are visible.
 * 4. Line one and sign disappear.
 * 5. Info wrapper moves to corner.
 * 6. Line two and sign appear.
 * 7. Background disappears.
 */
export function signLoaderModeUpdate(mode) {
  return { type: SIGN_LOADER_MODE_UPDATE, data: { mode } };
}

/**
 * Action to change the keypad mode.
 *
 * @param {number} mode -
 * 0. Panel invisible.
 * 1. Panel visible.
 * 2. Keypad visible.
 * 3. Info invisible.
 * 4. Keys open.
 * 5. Keys visible.
 * 6. Keypad invisible.
 * 7. Confirmation visible.
 * 8. Check visible.
 * 9. Check invisible.
 * 10. Thank You visible.
 */
export function signKeypadModeUpdate(mode) {
  return { type: SIGN_KEYPAD_MODE_UPDATE, data: { mode } };
}

/** Action to start keypad keys appearance animation. */
export function signKeypadKeysShow() {
  return { type: SIGN_KEYPAD_KEYS_SHOW };
}

/** Action to clear number from keypad. */
export function signKeypadClear() {
  return { type: SIGN_KEYPAD_CLEAR };
}

/**
 * Action to add a single number to keypad.
 *
 * @param {string} key
 */
export function signKeypadKeyAdd(key) {
  return { type: SIGN_KEYPAD_KEY_ADD, data: { key } };
}

/** Action to remove a single number from keypad. */
export function signKeypadKeyRemove() {
  return { type: SIGN_KEYPAD_KEY_REMOVE };
}

/**
 * Action to start redirect timer on sign page.
 *
 * @param {SignRedirectData} data
 */
export function signRedirectStart({ redirect, duration }) {
  return {
    type: SIGN_REDIRECT_START,
    data: { redirect, duration }
  };
}

/** Action to stop redirect timer on sign page. */
export function signRedirectStop() {
  return { type: SIGN_REDIRECT_STOP };
}

/** Action to reset sign data. */
export function signReset() {
  return { type: SIGN_RESET };
}

/**
 * Action to request sign data.
 *
 * @param {SignRequestData} data
 */
export function requestSign({ id, redirect, duration }) {
  return { type: SIGN_FETCH_REQUESTED, data: { id, redirect, duration } };
}

/**
 * Action to request vendor data.
 *
 * @param {string} id
 */
export function requestVendor(id) {
  return { type: VENDOR_FETCH_REQUESTED, data: { id } };
}

/**
 * Action to send phone number to backend.
 *
 * @param {string} number
 * @param {string} redirect
 */
export function sendNumber(number, redirect) {
  return { type: SEND_NUMBER_REQUESTED, data: { number, redirect } };
}

/**
 * Action to update window dimensions.
 *
 * @param {DimensionsData} dimensions
 */
export function updateDimensions(dimensions) {
  return { type: DIMENSIONS_UPDATE, data: { dimensions } };
}

/**
 * Action to update scroll position.
 *
 * @param {ScrollData} scroll
 */
export function updateScroll(scroll) {
  return { type: SCROLL_UPDATE, data: { scroll } };
}



// WEBPACK FOOTER //
// ./src/actions/index.js